/**
 * Change-feed → query-key mapping. Pure, so every decision here is unit-testable.
 *
 * `GET /changes` reports which DOMAINS moved; this decides which TanStack Query keys that should
 * invalidate. It is the last mile of `docs/realtime-updates-plan-v1.md`: without it the publisher and
 * reader are both live and nothing on screen changes.
 *
 * ## Why a mapping table rather than "invalidate everything"
 *
 * `main.tsx` deliberately sets `refetchOnWindowFocus: false` because blanket focus-refetching was "the
 * dominant driver of the 'feels laggy' symptom" — it re-mints every presigned preview URL. So the value
 * here is precisely that it is NARROW: an order landing refreshes the order screens and nothing else.
 */
import type { ChangeDomain } from '@fsp/contracts';

export type DomainRevisions = Partial<Record<ChangeDomain, number>>;

/**
 * Query-key roots each domain invalidates. A key may appear under several domains — `press-worklist`
 * moves when a batch changes AND when a garment settles (an order-line write), so it is listed under
 * both rather than guessing which write the staffer cares about.
 */
export const DOMAIN_QUERY_KEYS: Record<ChangeDomain, readonly string[]> = {
  orders: [
    'orders',
    'order',
    'production-board',
    'order-reprints',
    // The reprint queue lives at PK `REPRINTQ#`, which change-cursor.ts maps to `orders` — so this
    // page rides the orders domain, not batches. Listing it under batches would look right and
    // never update.
    'reprint-queue',
    'caps',
    'fulfill',
    'press-worklist',
    // Production events (R2) live under ORDER# partitions, so their writes publish this domain —
    // both the job-card History and the /activity feed ride it with no extra transport.
    'order-events',
    'activity',
    // The ship station's pack manifest (S3). Every row it reads — the shipment records, the
    // SHIPVERIFY ticks, the fulfillment marker — is an SK under `ORDER#<store>#<order>`, so its
    // writes publish the ORDERS domain and the manifest refreshes with no extra transport. This
    // matters at the bench: a reprint queued at the press station must darken the ship gate under
    // the packer's hands, not on the next manual refresh.
    'ship-manifest',
  ],
  batches: [
    'batches',
    'batch',
    'batch-manifest',
    'production-board',
    'order-reprints',
    'press-worklist',
  ],
  designs: ['designs'],
  inventory: [
    'inventory',
    'inventory-overview',
    'inventory-inbound',
    'inventory-purchase-draft',
    'inventory-usage',
    // Claims / corrections / settles move with blank-LEDGER writes (RECV/RECVADJ/resolve markers all
    // publish the inventory domain), so every 'receiving' query — the claims worklist, the undo
    // panels, and the hub's claims KPI — live-updates with them (P3: the claims count is now surfaced
    // at hub level, so staleness there would misreport money S&S owes).
    'receiving',
    // Stock items (P2b) — ITEM# writes publish the inventory domain (utils PK_PREFIX_DOMAINS).
    'stock-items',
    // Garment stock (finished-garment-stock v1) — GARMENTSTOCK# writes publish inventory too.
    'garment-stock',
  ],
  transfers: ['transfer-inventory'],
  rolls: ['rolls'],
  catalog: [
    'catalog',
    'catalog-detail',
    'catalog-caps',
    'catalog-nonapparel',
    'catalog-sync',
    'caps',
  ],
  purchaseOrders: ['purchase-orders', 'inventory-inbound', 'inventory-purchase-draft'],
  stores: ['stores'],
  /** The viewer reads through the loader, not a query — nothing to invalidate. */
  pricingConfig: [],
  // PRESS# rows publish `config` too (utils PK_PREFIX_DOMAINS) — the presses page + the spec editor's picker.
  config: ['reorder-config', 'shipto-config', 'payment-profile-config', 'presses'],
};

/**
 * ⚠️ **MONEY PATH — keys that may be marked stale but must NEVER be auto-refetched.**
 *
 * `press-station-page.tsx:235` drives its whole list from a single `['press-worklist']` query and
 * optimistically mutates it (`cancelQueries` + `setQueryData` at `:278`). It is the ONLY optimistically
 * controlled key in the dashboard — this set was derived by grepping for that, not guessed.
 *
 * Auto-refetching it would be actively dangerous in two ways:
 *  1. **A mis-tap that spends money.** Refreshed counts reorder the list, so a `BatchCard` can unmount
 *     from under a finger and the tap lands on a different garment — firing `settleLine`, which consumes
 *     a blank, against the wrong one.
 *  2. **A money action that appears to fail.** A background refetch overwrites the optimistic tick with
 *     server state that may not yet include the just-sent mutation, so the checkbox visibly reverts and
 *     the operator taps again.
 *
 * So it gets `refetchType: 'none'`: marked invalid, refetched on the next natural remount. The press
 * kiosk therefore still does NOT live-update — that needs the pending-write overlay (plan Phase 2) and
 * the kiosk live layer (Phase 6), both deliberately gated. Do not "fix" this by removing the entry.
 */
export const MARK_STALE_ONLY: ReadonlySet<string> = new Set(['press-worklist']);

/**
 * Query-key roots deliberately NOT driven by the change feed, with the reason. Anything not here and not
 * in `DOMAIN_QUERY_KEYS` fails `change-feed.test.ts` — so a new screen cannot quietly ship a query that
 * never refreshes, which is the whole bug class this work exists to kill.
 */
export const UNMAPPED_QUERY_KEYS: ReadonlyMap<string, string> = new Map([
  ['style-variants', 'S&S Activewear lookup — external data, our writes cannot change it'],
  ['style-colors', 'S&S Activewear lookup — external data'],
  ['style-search', 'S&S Activewear lookup — external data'],
  [
    'blank-style-pairs',
    // The mapping editor's derived garment quick-pick. It IS computed from our own mapping writes, so
    // unlike the S&S lookups above it is not externally owned — but the server caches it for 5 minutes,
    // so a feed-driven refetch would re-fetch the same cached response and change nothing on screen.
    // Staleness is harmless by construction: the list is an accelerator whose worst case is that a pair
    // someone created this minute isn't offered yet, and both style fields keep their full search.
    'Derived garment presets — server-cached for minutes, so a live refetch cannot change the answer',
  ],
  [
    'resend-preview',
    // P7-5. DELIBERATELY NOT LIVE, and this is a money decision rather than an oversight. The re-send
    // preview is a SPEND SNAPSHOT the operator is deciding against, and its `rebuildFingerprint` is the
    // token their confirm must echo — so a feed-driven refetch mid-dialog would move the numbers under
    // them AND silently invalidate the confirm they were about to make. The dialog refetches on every
    // open (staleTime/gcTime 0) and offers an explicit re-check, which is the same reasoning as the
    // press station's manual Refresh: auto-refresh on a surface where a tap spends money is a hazard,
    // not a feature.
    'P7-5 re-send preview — a spend snapshot; auto-refetch would invalidate the operator’s pending confirm',
  ],
  [
    'count-shelf',
    // The shelf count's frozen snapshot. Same reasoning as the two above, one step further: a walk
    // takes minutes and the operator is keying numbers against rows by POSITION. A feed-driven
    // refetch would re-run `buildCountSheet`, and a blank whose on-hand changed mid-walk (a press
    // settling, a receipt landing) can move rows under the thumb — the press-station lesson, where an
    // auto-refetch reordering rows turned a mis-tap into a money write. The page offers an explicit
    // "Reload sheet" instead. Staleness is harmless here by construction: the cached figure is only
    // ever a variance baseline and a plausibility check, never the number posted. The hub's pull
    // items (`['count-shelf', 'hub']`) and Restock's Set read (`['count-shelf', 'restock']`) share the
    // root as short-lived reads of the same count sheet: the hub's refreshes on its Refresh button and
    // every 5 minutes, Restock's on every open of Set — neither is a worklist, neither posts a cached
    // quantity, and a feed-driven refetch would only re-read what the next open reads anyway.
    'Shelf count — a positional worklist; a mid-walk refetch could reorder rows under a money tap',
  ],
]);

/** Every key the feed may refetch (i.e. excluding the money-path holdouts). */
export function allRefetchableQueryKeys(): string[] {
  const keys = new Set<string>();
  for (const list of Object.values(DOMAIN_QUERY_KEYS)) {
    for (const k of list) if (!MARK_STALE_ONLY.has(k)) keys.add(k);
  }
  return [...keys].sort();
}

/**
 * Domains whose revision moved.
 *
 * A domain missing from either side counts as unchanged rather than changed: the reader always sends a
 * complete map, so an absent key means an older/newer client shape, and guessing "changed" there would
 * fire a spurious refresh on every poll.
 */
export function changedDomains(
  prev: DomainRevisions,
  next: DomainRevisions,
): (keyof DomainRevisions)[] {
  const domains = new Set([...Object.keys(prev), ...Object.keys(next)] as ChangeDomain[]);
  return [...domains]
    .filter((d) => {
      const a = prev[d];
      const b = next[d];
      if (a === undefined || b === undefined) return false;
      return a !== b;
    })
    .sort();
}

/** Split the keys for a set of changed domains into refetch-now vs mark-stale-only. */
export function invalidationPlan(domains: readonly ChangeDomain[]): {
  refetch: string[];
  markStale: string[];
} {
  const keys = new Set<string>();
  for (const d of domains) {
    for (const k of DOMAIN_QUERY_KEYS[d]) keys.add(k);
  }
  const refetch: string[] = [];
  const markStale: string[] = [];
  for (const k of [...keys].sort()) {
    if (MARK_STALE_ONLY.has(k)) markStale.push(k);
    else refetch.push(k);
  }
  return { refetch, markStale };
}
